import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
  font-size: 24px;
`;


const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const TableHeader = styled.th`
  background: #f8f9fa;
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #dee2e6;
`;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
  color: #333;
`;

const ContactLink = styled.a`
  color: #007AFF;
  text-decoration: none;
  display: block;
  margin-bottom: 4px;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const LeadScore = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  
  &.high {
    background: #d4edda;
    color: #155724;
  }
  
  &.medium {
    background: #fff3cd;
    color: #856404;
  }
  
  &.low {
    background: #f8d7da;
    color: #721c24;
  }
`;


const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  
  h3 {
    margin-bottom: 10px;
  }
  
  p {
    margin-bottom: 20px;
  }
`;

const Renters = () => {
  const [renters, setRenters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRenters();
  }, []);

  const loadRenters = async () => {
    try {
      const data = await showingbookerAPI.getRentersContacts();
      setRenters(data);
    } catch (error) {
      console.error('Error loading renters:', error);
    } finally {
      setLoading(false);
    }
  };


  const getLeadScoreClass = (score) => {
    if (score >= 75) return 'high';
    if (score >= 50) return 'medium';
    return 'low';
  };

  const formatBudget = (min, max) => {
    if (!min && !max) return 'Not specified';
    if (min && max) return `$${parseInt(min).toLocaleString()} - $${parseInt(max).toLocaleString()}`;
    if (min) return `$${parseInt(min).toLocaleString()}+`;
    if (max) return `Up to $${parseInt(max).toLocaleString()}`;
    return 'Not specified';
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading renters...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>🏠 Renter Prospects from Showings ({renters.length})</Title>
        <p style={{ color: '#666', margin: '8px 0 0 0', fontSize: '14px' }}>
          Renters who viewed your properties through ShowingBooker
        </p>
      </Header>

      {renters.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '48px', marginBottom: '16px', opacity: '0.5' }}>🏠</div>
          <h3>No Renter Prospects Yet</h3>
          <p>Renters who view your properties will appear here automatically for tracking and follow-up.</p>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Contact</TableHeader>
              <TableHeader>Budget</TableHeader>
              <TableHeader>Preferences</TableHeader>
              <TableHeader>Lead Score</TableHeader>
              <TableHeader>Source</TableHeader>
            </tr>
          </thead>
          <tbody>
            {renters.map((renter) => (
              <TableRow key={renter.id}>
                <TableCell>
                  <strong>{renter.first_name} {renter.last_name}</strong>
                  {renter.lead_source && <div style={{fontSize: '12px', color: '#666'}}>Source: {renter.lead_source}</div>}
                </TableCell>
                <TableCell>
                  {renter.email && (
                    <ContactLink href={`mailto:${renter.email}`}>
                      {renter.email}
                    </ContactLink>
                  )}
                  {renter.phone && (
                    <ContactLink href={`tel:${renter.phone}`}>
                      {renter.phone}
                    </ContactLink>
                  )}
                </TableCell>
                <TableCell>
                  {formatBudget(renter.budget_min, renter.budget_max)}
                  {renter.move_in_date && (
                    <div style={{fontSize: '12px', color: '#666'}}>
                      Move-in: {new Date(renter.move_in_date).toLocaleDateString()}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {(renter.bedrooms_desired || renter.bathrooms_desired) && (
                    <div>{renter.bedrooms_desired}BR / {renter.bathrooms_desired}BA</div>
                  )}
                  {renter.preferred_areas && (
                    <div style={{fontSize: '12px', color: '#666'}}>{renter.preferred_areas}</div>
                  )}
                  {renter.pets && <div style={{fontSize: '12px', color: '#666'}}>Has pets</div>}
                </TableCell>
                <TableCell>
                  <LeadScore className={getLeadScoreClass(renter.lead_score)}>
                    {renter.lead_score}/100
                  </LeadScore>
                </TableCell>
                <TableCell>
                  <div style={{ 
                    color: '#666', 
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}>
                    ShowingBooker
                  </div>
                  {renter.created_at && (
                    <div style={{ 
                      color: '#999', 
                      fontSize: '12px' 
                    }}>
                      {new Date(renter.created_at).toLocaleDateString()}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}

    </Container>
  );
};

export default Renters;